import React, { useRef, useState } from 'react';
import CommonButton from '../../../../Components/CommonButton';
import CommonTable from '../../../../Components/CommonTable';
import CommonModal from '../../../../Components/CommonModal';
import { useConfirm } from '../../../../Components/ConfirmModal';
import Section from './Section';
import {
    risk_portal_create_treatment,
    risk_portal_update_treatment,
    risk_portal_delete_treatment,
    risk_portal_approve_treatment,
    risk_portal_reject_treatment,
    risk_portal_upload_evidence,
    risk_portal_delete_evidence,
} from '../../../../services';
import { Body } from '../../../../Constants/Fonts';
import { extractItem, STRATEGY_OPTIONS, LIKELIHOOD_LABELS, IMPACT_LABELS, titleCase, fmtDate, fmtDateTime, evidenceDisplayName } from './constants';

// An evidence record is considered linked to a treatment plan when any of these
// fields matches the plan id — the upload tags the file with `treatment_id`.
const evidenceTreatmentId = (ev) => ev?.treatment_id ?? ev?.treatment ?? ev?.treatment_plan ?? null;

// Reject-plan comments are posted with this prefix (see RiskDetail.doReject), so
// the Treatment stage can surface the reviewer's rejection reason to the owner.
const REJECT_PREFIX = 'Treatment plan rejected:';

const APPROVER_ROLES = ['risk_reviewer', 'business_owner', 'ciso', 'risk_admin'];

// TreatmentSection — the treatment-plan list (with approve/reject/delete) plus
// the "Create Treatment Plan" form rendered INLINE below the list (no modal),
// so the stage's form sits directly under the stepper.
export default function TreatmentSection({ riskId, detail, role, reload, notify, submitStage, canSubmit, stageBtnText, busy: stageBusy }) {
    const [busy, setBusy] = useState(false);
    // The treatment plan currently being edited (opens the edit modal). null = closed.
    const [editing, setEditing] = useState(null);
    // Optional user-given label for the evidence — sent as its `name`/`description`
    // so it shows in the Evidence list. Only applied when a SINGLE file is staged;
    // with multiple files each keeps its own file name.
    const [evidenceName, setEvidenceName] = useState('');
    // The chosen evidence files are STAGED here (not uploaded on select) — multiple
    // allowed; all upload together with the treatment plan when "Save & Next" is
    // clicked.
    const [evidenceFiles, setEvidenceFiles] = useState([]);
    const fileRef = useRef(null);
    // Lets the single external "Save & Next" button trigger the inline plan
    // form's own validation + submit (see CommonModal's formRef escape hatch).
    const treatmentFormRef = useRef(null);
    const { confirm, confirmModal } = useConfirm();
    const treatments = detail.treatments || [];
    // A plan already exists → this is the revision/reject pass: the owner may
    // edit the existing plan (row Edit) OR add a new one, so the inline form is
    // OPTIONAL. The first time through (no plan yet) the form is the only way to
    // supply one, so its fields are required.
    const hasExisting = treatments.length > 0;
    const evidence = detail.evidence || [];
    // Rejection reasons left by a reviewer (newest first) — shown as a banner so
    // the owner knows what to fix before resubmitting.
    const rejectionComments = (detail.comments || [])
        .filter((c) => typeof c.text === 'string' && c.text.startsWith(REJECT_PREFIX))
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    // Evidence names for the treatment table's Evidence column — the entered name
    // with the file's extension appended (e.g. "ssss.pdf"). See evidenceDisplayName.
    const evidenceNames = evidence.map(evidenceDisplayName).filter(Boolean);
    const canApprove = APPROVER_ROLES.includes(role);

    const run = async (fn, ok) => {
        setBusy(true);
        try { await fn(); if (ok) notify(ok, 'success'); await reload(); }
        catch (e) { notify(e?.response?.data?.error?.message || e?.message || 'Action failed', 'error'); }
        finally { setBusy(false); }
    };

    // Single "Save & Next" for the Treatment stage: it creates the plan (and
    // uploads any staged evidence) AND advances the workflow — one button, all
    // APIs. The stage progression itself is delegated to the parent's
    // submitStage(saveFn), so a reshuffled pipeline (approval/terminal stage,
    // reordered next_stage) still does the right thing — this section only owns
    // the "create the plan" half of the save.
    const onStageSubmit = (d) => {
        // Whether the inline form is actually a NEW plan to create. First pass
        // (no existing plan) the fields are required, so there is always one. In
        // the revision/reject pass the form is optional — create only if the
        // owner filled it; an empty form just means "I only edited the existing
        // plan above", so we advance without adding a duplicate.
        const wantsNewPlan = !hasExisting
            || Boolean((d.description || '').trim())
            || Boolean(d.target_date)
            || evidenceFiles.length > 0;
        submitStage(async () => {
            if (!wantsNewPlan) return;
            // Capture the created plan's id so its evidence can be tagged with it —
            // that link is what lets deleting the plan cascade-delete its evidence.
            const created = extractItem(await risk_portal_create_treatment(riskId, {
                strategy: d.strategy,
                target_date: d.target_date || null,
                description: (d.description || '').trim(),
                actions: [],
            }));
            const createdId = created?.id ?? null;
            // Upload every staged file (one request each). A single file can take
            // the typed Evidence Name; with multiple files each keeps its own name.
            for (const file of evidenceFiles) {
                const label = (evidenceFiles.length === 1 && evidenceName.trim()) ? evidenceName.trim() : file.name;
                const fd = new FormData();
                fd.append('file', file);
                fd.append('risk_id', riskId);
                fd.append('name', label);
                fd.append('description', label);
                if (createdId != null) fd.append('treatment_id', createdId);
                await risk_portal_upload_evidence(fd);
            }
        });
    };
    // Save an edited plan (opened via the row's Edit button) — used after a reject
    // sends the risk back here so the owner can revise strategy/date/description
    // and resubmit for review.
    const saveEdit = (d) => run(async () => {
        await risk_portal_update_treatment(editing.id, {
            strategy: d.strategy,
            target_date: d.target_date || null,
            description: (d.description || '').trim(),
        });
        setEditing(null);
    }, 'Treatment plan updated');
    const approve = (t) => run(() => risk_portal_approve_treatment(t.id), 'Treatment approved');
    const reject = (t) => {
        const reason = window.prompt('Reason for sending the plan back for revision:');
        if (reason === null) return;
        if (!reason.trim()) { notify('A reason is required to reject', 'error'); return; }
        run(() => risk_portal_reject_treatment(t.id, { reason }), 'Treatment sent back for revision');
    };
    const remove = async (t) => {
        // Deleting a plan also deletes the evidence attached to it. Prefer the
        // precise link (evidence tagged with this plan's treatment_id). If NO
        // evidence on the risk carries any treatment link (legacy uploads, or the
        // backend doesn't persist the tag), fall back to the risk's whole evidence
        // set — in this portal evidence is only ever attached while creating a plan.
        const anyLinked = evidence.some((ev) => evidenceTreatmentId(ev) != null);
        const linked = evidence.filter((ev) => String(evidenceTreatmentId(ev)) === String(t.id));
        const toDelete = anyLinked ? linked : evidence;
        const msg = toDelete.length
            ? `Delete this treatment plan and its ${toDelete.length} evidence file${toDelete.length > 1 ? 's' : ''}?`
            : 'Delete this treatment plan?';
        const ok = await confirm({ title: 'Delete treatment', message: msg, confirmLabel: 'Delete', danger: true, themeColor: false });
        if (!ok) return;
        run(async () => {
            for (const ev of toDelete) {
                await risk_portal_delete_evidence(ev.id);
            }
            await risk_portal_delete_treatment(t.id);
        }, 'Treatment plan deleted');
    };

    // Evidence file select — STAGES the chosen file(s) (no upload yet), APPENDING
    // to any already picked so the user can add in several goes. The actual upload
    // happens in `onStageSubmit()` when "Save & Next" is clicked.
    const selectEvidence = (e) => {
        const picked = Array.from(e.target.files || []);
        e.target.value = '';
        if (picked.length) setEvidenceFiles((prev) => [...prev, ...picked]);
    };
    const removeEvidenceAt = (idx) => setEvidenceFiles((prev) => prev.filter((_, i) => i !== idx));

    const rows = treatments.map((t) => ({
        data: [
            <Body as="span" className="rp1_badge rp1_badge--info">{titleCase(t.strategy)}</Body>,
            t.status
                ? <Body as="span" className="rp1_badge rp1_badge--neutral">{titleCase(t.status)}</Body>
                : <Body as="span" variant={2} color="var(--color-text-muted)">—</Body>,
            t.description || '—',
            evidenceNames.length > 0
                ? <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {evidenceNames.map((n, i) => (
                        <Body as="span" key={i} variant={2} color="var(--color-text-primary)">{n}</Body>
                    ))}
                </div>
                : <Body as="span" variant={2} color="var(--color-text-muted)">—</Body>,
            <div style={{ display: 'inline-flex', gap: 6 }}>
                {canApprove && t.status && !['approved', 'rejected'].includes(t.status) && (
                    <>
                        <CommonButton variant="success" text="Approve" onClick={() => approve(t)} disable={busy} />
                        <CommonButton variant="error" text="Reject" onClick={() => reject(t)} disable={busy} />
                    </>
                )}
                {canSubmit && <CommonButton variant="secondary" text="Edit" onClick={() => setEditing(t)} disable={busy} />}
                <CommonButton variant="error" text="Delete" onClick={() => remove(t)} disable={busy} />
            </div>,
        ],
    }));

    // Stage 1 (Assessment) values the user filled in — shown read-only here as a
    // full key : value panel for context while planning treatment (mirrors the
    // Review stage). Short values → chips; long text → full-width blocks. For
    // likelihood / impact the chosen scale word (e.g. "Almost Certain") is appended.
    const a = detail.assessment || {};
    const scaleVal = (n, labels) => (n >= 1 && n <= labels.length ? `${n} — ${labels[n - 1]}` : (n != null ? String(n) : undefined));
    const assessmentItems = [
        ['Category', detail.risk_type ? titleCase(detail.risk_type) : undefined],
        ['Risk Owner', detail.risk_owner],
        ['Identification Date', detail.target_date ? fmtDate(detail.target_date) : undefined],
        ['State', detail.state ? (detail.state_label || titleCase(detail.state)) : undefined],
        ['Business Unit', detail.business_unit_name || (detail.business_unit ? String(detail.business_unit) : undefined)],
        ['Project', detail.project_name],
        ['Inherent Likelihood', scaleVal(a.inherent_likelihood ?? detail.likelihood, LIKELIHOOD_LABELS)],
        ['Inherent Impact', scaleVal(a.inherent_impact ?? detail.impact, IMPACT_LABELS)],
        ['Residual Likelihood', scaleVal(a.residual_likelihood, LIKELIHOOD_LABELS)],
        ['Residual Impact', scaleVal(a.residual_impact, IMPACT_LABELS)],
        ['Control Effectiveness', a.control_effectiveness != null ? `${a.control_effectiveness}%` : undefined],
        ['Risk Velocity', a.risk_velocity ? titleCase(a.risk_velocity) : undefined],
    ].filter(([, v]) => v != null && v !== '');
    // Long text values → full-width label-on-top blocks.
    const assessmentLongItems = [
        ['Risk Description', detail.risk],
        ['Threat', detail.threat],
        ['Vulnerability', detail.vulnerability],
        ['Notes', a.notes],
    ].filter(([, v]) => v != null && v !== '');

    return (
        <>
            {/* Returned-for-revision banner — the reviewer's rejection note(s),
                surfaced here (gradient header + key:value rows) so the owner sees
                what to fix before resubmitting. */}
            {rejectionComments.length > 0 && (
                <div className="rp1_revision">
                    <div className="rp1_revision_head">
                        <span className="rp1_revision_icon" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 14 4 9 9 4" />
                                <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
                            </svg>
                        </span>
                        <div style={{ minWidth: 0 }}>
                            <Body as="div" weight="semibold" color="#ffffff" style={{ fontSize: 15 }}>Returned for revision</Body>
                            <Body as="div" variant={2} style={{ color: 'rgba(255,255,255,0.85)' }}>
                                {rejectionComments.length === 1
                                    ? 'The reviewer sent this plan back. Address the note below, then Save & Next.'
                                    : `${rejectionComments.length} review notes to address before resubmitting.`}
                            </Body>
                        </div>
                    </div>
                    <div className="rp1_revision_body">
                        {rejectionComments.map((c) => {
                            const author = c.author || c.created_by || '';
                            const kv = [
                                ['Reason', c.text.slice(REJECT_PREFIX.length).trim() || '—'],
                                ...(author ? [['Reviewer', author]] : []),
                                ...(c.created_at ? [['Returned on', fmtDateTime(c.created_at)]] : []),
                            ];
                            return (
                                <div key={c.id} className="rp1_revision_item">
                                    {kv.map(([label, value]) => (
                                        <div key={label} className="rp1_tp_row">
                                            <span className="rp1_tp_label">{label}</span>
                                            <span className="rp1_tp_value"><Body as="span">{value}</Body></span>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {/* Stage 1 (Assessment) data — just the values entered in the form,
                as a key : value list (label left, value right). */}
            <Section title="Risk Assessment">
                {assessmentItems.length === 0 && assessmentLongItems.length === 0 ? (
                    <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 13 }}>No assessment data yet.</p>
                ) : (
                    <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-xl)', padding: '20px 22px' }}>
                        {assessmentItems.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', columnGap: 32, rowGap: 20 }}>
                                {assessmentItems.map(([label, value]) => (
                                    <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
                                        <span className="rp1_field_label">{label}</span>
                                        <Body as="span" color="var(--color-text-primary)" weight="medium" style={{ fontSize: 14, wordBreak: 'break-word' }}>{value}</Body>
                                    </div>
                                ))}
                            </div>
                        )}
                        {assessmentLongItems.length > 0 && (
                            <div style={{
                                display: 'flex', flexDirection: 'column', gap: 18,
                                ...(assessmentItems.length > 0 ? { marginTop: 22, paddingTop: 20, borderTop: '1px solid var(--color-border)' } : {}),
                            }}>
                                {assessmentLongItems.map(([label, value]) => (
                                    <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        <span className="rp1_field_label">{label}</span>
                                        <Body as="span" color="var(--color-text-primary)" style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{value}</Body>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </Section>

            <Section title="Treatment Plans" count={treatments.length}>
                {treatments.length > 0 && (
                    <CommonTable headers={['Strategy', 'Status', 'Description', 'Evidence', 'Action']} rows={rows} noPadding noScroll />
                )}

                {treatments.length === 0 && !canSubmit && (
                    <p style={{ margin: 0, color: 'var(--color-text-muted)', fontSize: 13 }}>No treatment plans yet.</p>
                )}

                {/* Create form + stage progression only render for the active
                    stage owner. When merely viewing this stage (or not its owner),
                    the plan list above stays read-only — no create form, no advance. */}
                {canSubmit && (
                    <>
                        {/* Single treatment-plan form — the shared CommonModal rendered
                            INLINE. There's no separate "Add Treatment Plan" button: the
                            form's submit IS the stage's "Save & Next", which creates the
                            plan (+ evidence) and advances the workflow in one click.
                            First pass (no plan yet) the fields are required; on a reject
                            revision pass (a plan already exists) they're optional, since
                            the owner may just edit the existing plan above and move on. */}
                        <CommonModal
                            inline
                            formRef={treatmentFormRef}
                            title={hasExisting ? 'Add Another Treatment Plan (optional)' : 'Provide Treatment Plan'}
                           
                            fields={[
                                { name: 'strategy', label: 'Treatment Strategy', required: !hasExisting, type: 'select', half: true, options: STRATEGY_OPTIONS },
                                { name: 'target_date', label: 'Target Date', required: !hasExisting, type: 'date', half: true },
                                { name: 'description', label: 'Treatment Action', required: !hasExisting, type: 'textarea', placeholder: 'Describe the treatment plan…', rows: 3 },
                            ]}
                            defaultValues={{ strategy: 'mitigate', target_date: '', description: '' }}
                            onSubmit={onStageSubmit}
                            buttons={[]}
                        >
                            {/* Evidence — sits right below the Treatment Action field.
                                The file is STAGED on select; the create + upload APIs are
                                both called when "Save & Next" is clicked. */}
                            <div className="cmn-modal-field" style={{ marginTop: 16 }}>
                                <label className="cmn-modal-label">Evidence Name</label>
                                <input className="cmn-modal-input" type="text" value={evidenceName}
                                    placeholder={evidenceFiles.length > 1 ? 'Multiple files — each keeps its own name' : 'Give this evidence a name…'}
                                    disabled={busy || stageBusy || evidenceFiles.length > 1}
                                    onChange={(e) => setEvidenceName(e.target.value)} />
                            </div>
                            <div className="cmn-modal-field" style={{ marginTop: 12 }}>
                                <label className="cmn-modal-label">Evidence</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <div>
                                        <CommonButton type="button" variant="secondary" text={evidenceFiles.length ? 'Add More Files' : 'Upload Evidence'} onClick={() => fileRef.current?.click()} disable={busy || stageBusy} />
                                    </div>
                                    {evidenceFiles.map((file, i) => (
                                        <div key={`${file.name}-${file.size}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                            <Body as="span" variant={2} color="var(--color-text-secondary)">{file.name}</Body>
                                            <CommonButton type="button" variant="error" text="Remove" onClick={() => removeEvidenceAt(i)} disable={busy || stageBusy} />
                                        </div>
                                    ))}
                                    <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip" onChange={selectEvidence} />
                                </div>
                            </div>
                        </CommonModal>
                        {/* The single stage-progression control. It triggers the inline
                            form's own validation + submit (onStageSubmit), which creates
                            the plan (+ evidence) when needed and then advances the
                            workflow via the parent's submitStage — so one button does it
                            all, and a reshuffled pipeline still advances correctly. */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 12 }}>
                            <CommonButton text={stageBtnText} onClick={() => treatmentFormRef.current?.submit?.()} disable={stageBusy || busy} />
                        </div>
                    </>
                )}
                {confirmModal}

                {/* Edit an existing plan — opened from a row's Edit button. Pre-filled
                    with the plan's current values; saves via update_treatment. Keyed by
                    the plan id so switching rows re-initialises the form. */}
                {editing && (
                    <CommonModal
                        show
                        key={editing.id}
                        onHide={() => setEditing(null)}
                        title="Edit Treatment Plan"
                        subtitle="Revise the plan, save, then resubmit for review."
                        fields={[
                            { name: 'strategy', label: 'Treatment Strategy', required: true, type: 'select', half: true, options: STRATEGY_OPTIONS },
                            { name: 'target_date', label: 'Target Date', required: true, type: 'date', half: true },
                            { name: 'description', label: 'Treatment Action', required: true, type: 'textarea', rows: 3, placeholder: 'Describe the treatment plan…' },
                        ]}
                        defaultValues={{
                            strategy: editing.strategy || 'mitigate',
                            target_date: editing.target_date ? String(editing.target_date).slice(0, 10) : '',
                            description: editing.description || '',
                        }}
                        onSubmit={saveEdit}
                        buttons={[
                            { text: 'Cancel', variant: 'secondary', onClick: () => setEditing(null), disabled: busy },
                            { text: 'Save Changes', variant: 'primary', type: 'submit', loading: busy, loadingText: 'Saving…' },
                        ]}
                    />
                )}
            </Section>
        </>
    );
}
